import React from 'react';
import { Download, Play } from 'lucide-react';

const Hero: React.FC = () => {
  return (
    <section id="inicio" className="min-h-screen bg-gradient-to-br from-cream-50 via-cream-100 to-gold-100 pt-20">
      <div className="container mx-auto px-4 py-16">
        <div className="flex flex-col lg:flex-row items-center justify-between min-h-[80vh]">
          {/* Content */}
          <div className="lg:w-1/2 text-center lg:text-left mb-12 lg:mb-0">
            <div className="mb-6">
              <span className="inline-block px-4 py-2 bg-gold-100 text-gold-800 rounded-full text-sm font-semibold mb-4">
                ✝ Aplicativo Católico
              </span>
            </div>
            
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-display font-bold text-brown-800 leading-tight mb-6">
              Os santos estão{' '}
              <span className="text-gradient">online</span>.{' '}
              <br />
              Siga quem te leva ao{' '}
              <span className="text-gradient">Céu</span>.
            </h1>
            
            <p className="text-xl text-brown-600 mb-8 leading-relaxed">
              Conecte-se com a santidade todos os dias. Descubra histórias inspiradoras, 
              orações poderosas e uma jornada espiritual transformadora na palma da sua mão.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
              <a href="/signup" className="btn-primary flex items-center justify-center gap-2">
                <Download size={20} />
                Cadastre-se
              </a>
              <a href="#inscrever" className="flex items-center justify-center gap-2 px-6 py-3 border-2 border-brown-500 text-brown-700 hover:bg-brown-500 hover:text-white rounded-lg transition-colors duration-200 font-semibold">
                <Play size={20} />
                Saiba Mais
              </a>
            </div>
            
            <div className="mt-8 flex items-center justify-center lg:justify-start gap-6 text-sm text-brown-600">
              <div className="flex items-center gap-2">
                <span className="text-gold-500">✝</span>
                <span>Em breve</span>
              </div>
              <span>•</span>
              <span>100% Gratuito</span>
            </div>
          </div>

          {/* Phone Mockup */}
          <div className="lg:w-1/2 flex justify-center">
            <div className="relative">
              {/* Phone Frame */}
              <div className="w-80 h-[640px] bg-black rounded-[3rem] p-2 shadow-2xl">
                <div className="w-full h-full bg-cream-50 rounded-[2.5rem] overflow-hidden relative">
                  {/* Status Bar */}
                  <div className="flex justify-between items-center px-6 py-3 text-xs font-semibold text-brown-800">
                    <span>9:41</span>
                    <div className="flex gap-1">
                      <div className="w-4 h-2 bg-brown-800 rounded-sm"></div>
                      <div className="w-4 h-2 bg-brown-800 rounded-sm"></div>
                      <div className="w-6 h-2 bg-brown-800 rounded-sm"></div>
                    </div>
                  </div>
                  
                  {/* App Content */}
                  <div className="px-6 py-4">
                    {/* Header */}
                    <div className="flex items-center justify-between mb-6">
                      <div className="flex items-center gap-2">
                        <div className="w-6 h-4 border border-brown-300 rounded"></div>
                        <div className="w-6 h-4 border border-brown-300 rounded"></div>
                        <div className="w-6 h-4 border border-brown-300 rounded"></div>
                      </div>
                      <h2 className="text-lg font-display font-bold text-brown-800">SantoGram</h2>
                      <div className="w-6 h-6 border border-brown-300 rounded-full"></div>
                    </div>
                    
                    {/* Santo do Dia Card */}
                    <div className="bg-white rounded-xl p-4 mb-4 shadow-md border border-cream-200">
                      <h3 className="font-display font-bold text-brown-800 mb-2">Santo do Dia</h3>
                      <div className="flex items-center gap-3">
                        <div 
                          className="w-16 h-20 bg-gradient-to-b from-gold-200 to-brown-200 rounded-lg bg-cover bg-center"
                          style={{ backgroundImage: "url('/santo_do_dia.png')" }}
                        ></div>
                        <div className="flex-1">
                          <p className="text-sm text-brown-700 italic mb-2">"Quero passar meu céu fazendo o bem na terra."</p>
                          <div className="flex gap-2">
                            <span className="px-2 py-1 bg-cream-200 text-xs rounded text-brown-600">🙏 Amém</span>
                            <span className="px-2 py-1 bg-cream-200 text-xs rounded text-brown-600">📿 Interceda</span>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    {/* Features Grid */}
                    <div className="mb-4">
                      <h4 className="font-display font-semibold text-brown-800 mb-3">Devocionais</h4>
                      <div className="grid grid-cols-2 gap-3">
                        <div className="bg-white rounded-lg p-3 shadow-sm border border-cream-200">
                          <div 
                            className="w-full h-16 bg-gradient-to-b from-gold-100 to-cream-200 rounded mb-2 bg-cover bg-center"
                            style={{ backgroundImage: "url('/jesus_cristo.png')" }}
                          ></div>
                          <p className="text-xs text-brown-700 font-semibold">A paz esteja convosco.</p>
                          <p className="text-xs text-brown-500">Jesus Cristo</p>
                        </div>
                        <div className="bg-white rounded-lg p-3 shadow-sm border border-cream-200">
                          <div 
                            className="w-full h-16 bg-gradient-to-b from-brown-100 to-cream-200 rounded mb-2 bg-cover bg-top"
                            style={{ backgroundImage: "url('/santo_agostinho.png')" }}
                          ></div>
                          <p className="text-xs text-brown-700 font-semibold">Ama e faz o que quiseres.</p>
                          <p className="text-xs text-brown-500">Santo Agostinho</p>
                        </div>
                      </div>
                    </div>
                    
                    {/* Bottom Section */}
                    <div className="bg-white rounded-xl p-4 shadow-md border border-cream-200">
                      <div className="flex items-center gap-3 mb-3">
                        <div 
                          className="w-12 h-12 bg-gradient-to-b from-gold-200 to-brown-200 rounded-full bg-cover bg-center"
                          style={{ backgroundImage: "url('/santa_teresinha.png')" }}
                        ></div>
                        <div className="flex-1">
                          <h5 className="font-display font-bold text-brown-800">Santa Teresinha</h5>
                          <p className="text-xs text-brown-600">1873-1897 • Patrona das missões • Festa 1 de outubro</p>
                        </div>
                      </div>
                      <button className="w-full bg-gold-500 text-white py-2 rounded-lg text-sm font-semibold">
                        Rezar Novena
                      </button>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Floating Elements */}
              <div className="absolute -top-4 -left-4 w-16 h-16 bg-gold-200 rounded-full opacity-20 animate-pulse"></div>
              <div className="absolute -bottom-8 -right-8 w-24 h-24 bg-brown-200 rounded-full opacity-20 animate-pulse"></div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero; 